/**
 * Parts of the Photo Moderation list (2026-10-19, owner: "The UI is broken on the admin
 * panel and the banners are useless or confusing").
 *
 * Pure: everything arrives in props, so render-tests/moderation-one-place.render.test.tsx
 * draws them to HTML. A refused photo's thumbnail is its KEPT file — a preview, or for an
 * explicit photo only its server-rendered blur — and its status is its review decision,
 * never the bare "Pending" of the screening record. Every status carries its meaning.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, EyeOff, ImageIcon, Lock, MinusCircle, RotateCcw, Users, XCircle, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { categoryText, photoSubjectCategoryOf } from './photo-subject-categories';

export interface ModerationStatus {
  label: string;
  meaning: string;
  className: string;
  icon: LucideIcon;
}

/** Every status the list shows, with what it means. The legend lists them all. */
export const STATUS_MEANINGS = {
  waiting: {
    label: 'Waiting for review',
    meaning: "Refused by the automated screen. The photo is kept, out of the member's photos, until a reviewer decides it on its page.",
    className: 'bg-amber-100 text-amber-800',
    icon: Clock,
  },
  confirmed: {
    label: 'Refusal confirmed',
    meaning: "A reviewer confirmed the refusal. The photo is kept, out of the member's photos, until the member deletes the account.",
    className: 'bg-red-100 text-red-800',
    icon: XCircle,
  },
  restored: {
    label: 'Restored',
    meaning: "A reviewer restored the photo. It is back in the member's photos.",
    className: 'bg-green-100 text-green-800',
    icon: RotateCcw,
  },
  legalHold: {
    label: 'Legal hold',
    meaning: 'Kept outside the member\'s files for a child-safety report (NCMEC CyberTipline).',
    className: 'bg-violet-100 text-violet-800',
    icon: Lock,
  },
  expired: {
    label: 'Deleted (15-day rule)',
    meaning: 'Refused before 2026-10-19 and deleted under the former 15-day rule. No file is kept.',
    className: 'bg-muted text-muted-foreground',
    icon: Trash2,
  },
  approved: {
    label: 'Approved',
    meaning: 'A reviewer approved the photo (a false positive of the screen).',
    className: 'bg-green-100 text-green-800',
    icon: CheckCircle,
  },
  rejected: {
    label: 'Rejected',
    meaning: 'A reviewer rejected the photo in the old screening record.',
    className: 'bg-red-100 text-red-800',
    icon: XCircle,
  },
  checkError: {
    label: 'Check error',
    meaning: 'A check could not run. The photo was not refused for that check.',
    className: 'bg-muted text-muted-foreground',
    icon: MinusCircle,
  },
  passed: {
    label: 'Passed',
    meaning: 'Every check passed. The photo is in the member\'s photos.',
    className: 'bg-green-100 text-green-800',
    icon: CheckCircle,
  },
  notReviewed: {
    label: 'Not reviewed',
    meaning: 'A check failed and no reviewer has decided the record yet.',
    className: 'bg-amber-100 text-amber-800',
    icon: Clock,
  },
} as const satisfies Record<string, ModerationStatus>;

const BY_DECISION: Record<string, ModerationStatus> = {
  pending: STATUS_MEANINGS.waiting,
  confirmed: STATUS_MEANINGS.confirmed,
  restored: STATUS_MEANINGS.restored,
  legal_hold: STATUS_MEANINGS.legalHold,
  expired: STATUS_MEANINGS.expired,
};

export interface StatusInput {
  admin_decision: string | null;
  checks: Array<{ result: string }>;
  quarantine: { decision: string } | null;
}

/** A refused photo's status is its review decision; any other photo keeps its screening status. */
export function moderationStatus(item: StatusInput): ModerationStatus {
  if (item.quarantine) return BY_DECISION[item.quarantine.decision] ?? STATUS_MEANINGS.notReviewed;
  if (item.admin_decision === 'approved') return STATUS_MEANINGS.approved;
  if (item.admin_decision === 'rejected') return STATUS_MEANINGS.rejected;
  const failed = item.checks.some((c) => c.result === 'fail');
  if (!failed && item.checks.some((c) => c.result === 'error')) return STATUS_MEANINGS.checkError;
  if (!failed) return STATUS_MEANINGS.passed;
  return STATUS_MEANINGS.notReviewed;
}

export function StatusBadge({ status }: { status: ModerationStatus }) {
  const Icon = status.icon;
  return (
    <span
      title={status.meaning}
      aria-label={`${status.label}: ${status.meaning}`}
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${status.className}`}
    >
      <Icon className="h-3 w-3" /> {status.label}
    </span>
  );
}

/** What each status means, in one place under the filters. */
export function StatusLegend() {
  return (
    <details className="rounded-lg border bg-card px-4 py-2 text-xs text-muted-foreground">
      <summary className="cursor-pointer select-none font-medium text-foreground">What each status means</summary>
      <dl className="mt-2 grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
        {Object.values(STATUS_MEANINGS).map((s) => (
          <div key={s.label} className="contents">
            <dt className="font-medium text-foreground">{s.label}</dt>
            <dd>{s.meaning}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

/** Why a list row has no image: the server sends a URL only for a file that exists. */
function noFileTitle(decision: string | null | undefined): string {
  if (decision === 'legal_hold') return "Kept outside the member's files for a child-safety report. Not shown in the list.";
  if (decision === 'confirmed' || decision === 'expired') return 'No file is kept for this photo.';
  return 'No file to show';
}

/**
 * The list thumbnail. A refused explicit photo shows ONLY its blur (a data: URL that
 * the server renders); no photo URL reaches the page until an audited reveal. A photo
 * with no file shows a placeholder that says why — never a broken image: an image that
 * fails to load (for example a file deleted after the list was read) falls back to it.
 */
export function QueueThumb({ thumbnailUrl, blurredThumbnail, decision }: {
  thumbnailUrl: string | null;
  blurredThumbnail: string | null;
  decision?: string | null;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (blurredThumbnail) {
    return (
      <div className="relative h-12 w-12 overflow-hidden rounded-lg bg-muted" title="Explicit photo, blurred. Open it to reveal it (logged).">
        <img src={blurredThumbnail} alt="Blurred refused photo" className="h-12 w-12 scale-110 object-cover blur-[3px]" />
        <span className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 text-[9px] font-medium text-white">
          <EyeOff className="h-3 w-3" /> Explicit
        </span>
      </div>
    );
  }
  if (thumbnailUrl && failedUrl !== thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt=""
        className="h-12 w-12 rounded-lg object-cover"
        onError={() => setFailedUrl(thumbnailUrl)}
      />
    );
  }
  const title = thumbnailUrl ? 'The file could not be loaded.' : noFileTitle(decision);
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted" title={title} aria-label={title}>
      <ImageIcon className="h-5 w-5 text-muted-foreground" />
    </div>
  );
}

/** "N refused": the member has several refused photos waiting; one click shows them together. */
export function MemberRefusalsChip({ count, onPick }: { count: number; onPick: () => void }) {
  if (count <= 1) return null;
  return (
    <button
      type="button"
      onClick={onPick}
      title="Show only this member's photos"
      className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-200"
    >
      <Users className="h-3 w-3" />{`${count} refused`}
    </button>
  );
}

/**
 * The flag chips of a row: what the screen refused the photo for. A photo_subject chip
 * names its CATEGORY (issue #3608). The single chip "Subject" could not tell a
 * child-safety flag from a photo of a meal, so the two `minor_primary` flags on PROD
 * waited 4 and 7 days and were found only with a SQL query. A severe category is filled,
 * not tinted, so it is seen without reading the row.
 */
export function FlagChips({ checks }: {
  checks: Array<{ log_id?: string; check_type: string; result: string; details?: unknown }>;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {checks.filter((c) => c.result === 'fail').map((c, i) => {
        // Only photo_subject carries a category; the other two checks keep their own name.
        const subject = c.check_type === 'photo_subject'
          ? categoryText(photoSubjectCategoryOf(c.details))
          : null;
        return (
          <span
            key={c.log_id ?? `${c.check_type}-${i}`}
            title={subject?.meaning}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              subject?.severe
                ? 'bg-red-600 text-white ring-1 ring-red-800'
                : c.check_type === 'explicit_content'
                  ? 'bg-red-100 text-red-800'
                  : c.check_type === 'photo_subject'
                    ? 'bg-violet-100 text-violet-800'
                    : 'bg-amber-100 text-amber-800'
            }`}
          >
            <AlertTriangle className="mr-0.5 h-3 w-3" />
            {subject?.chip
              ?? (c.check_type === 'explicit_content' ? 'Explicit' : c.check_type === 'photo_subject' ? 'Subject' : 'Contact info')}
          </span>
        );
      })}
    </div>
  );
}
